import { api } from "@/lib/api-client";

export type ContactSubmissionType = "GENERAL" | "COMMISSION";
export type ContactSubmissionStatus = "NEW" | "REVIEWING" | "ACCEPTED" | "DECLINED" | "COMPLETED" | "ARCHIVED";
export type CommissionType = "Vehicle Livery" | "Uniform Design" | "Custom Design" | "Other";

export interface ContactSubmissionInput {
  type: ContactSubmissionType;
  name: string;
  email: string;
  discord?: string | null;
  subject?: string | null;
  message: string;
  commissionType?: CommissionType | null;
  projectName?: string | null;
  communityName?: string | null;
  budget?: string | null;
  deadline?: string | null;
  referenceLinks?: string | null;
}

export interface ContactResponse {
  id: string;
  createdAt: string;
}

export interface ContactSubmission {
  id: string;
  type: ContactSubmissionType;
  name: string;
  email: string;
  discord: string | null;
  subject: string | null;
  message: string;
  commissionType: CommissionType | null;
  projectName: string | null;
  communityName: string | null;
  budget: string | null;
  deadline: string | null;
  referenceLinks: string | null;
  status: ContactSubmissionStatus;
  adminNotes: string | null;
  handledById: string | null;
  handledBy?: { id: string; displayName: string; email: string } | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ContactSubmissionFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: ContactSubmissionType | "ALL";
  status?: ContactSubmissionStatus | "ALL";
}

export interface UpdateContactSubmissionInput {
  status?: ContactSubmissionStatus;
  adminNotes?: string | null;
}

function listPath(filters: ContactSubmissionFilters) {
  const params = new URLSearchParams({
    page: String(filters.page ?? 1),
    pageSize: String(filters.pageSize ?? 10),
  });
  if (filters.search) params.set("search", filters.search);
  if (filters.type && filters.type !== "ALL") params.set("type", filters.type);
  if (filters.status && filters.status !== "ALL") params.set("status", filters.status);
  return `/contact/manage?${params.toString()}`;
}

export const contactApi = {
  submit: (input: ContactSubmissionInput) => api.post<ContactResponse>("/contact", input),
  list: (filters: ContactSubmissionFilters) => api.get<PaginatedResponse<ContactSubmission>>(listPath(filters)),
  detail: (id: string) => api.get<ContactSubmission>(`/contact/manage/${id}`),
  update: (id: string, input: UpdateContactSubmissionInput) =>
    api.patch<ContactSubmission>(`/contact/manage/${id}`, input),
  archive: (id: string) => api.patch<ContactSubmission>(`/contact/manage/${id}`, { status: "ARCHIVED" }),
};
